"use client";

import * as React from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ResponsiveCommandMultiSelectTrigger } from "./ResponsiveCommandMultiSelectTrigger";
import { ResponsiveCommandMultiSelectList } from "./ResponsiveCommandMultiSelectList";
import type { FloatingDropdownPosition } from "./useFloatingDropdown";
import type { SelectOption } from "./types";

type ListProps = React.ComponentProps<typeof ResponsiveCommandMultiSelectList>;

interface FloatingProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  containerRef: React.RefObject<HTMLDivElement>;
  dropdownRef: React.RefObject<HTMLDivElement>;
  triggerRef: React.RefObject<HTMLButtonElement>;
  dropdownPosition: FloatingDropdownPosition | null;
  selectedOptions: SelectOption[];
  placeholder: string;
  disabled: boolean;
  className?: string;
  title: string;
  listProps: ListProps;
}

/**
 * Inline expansion - floating variant: trigger stays, dropdown floats below.
 * Used in places like tickets/create where the trigger lives in tight layouts.
 */
export function ResponsiveCommandMultiSelectInlineFloating({
  open,
  setOpen,
  containerRef,
  dropdownRef,
  triggerRef,
  dropdownPosition,
  selectedOptions,
  placeholder,
  disabled,
  className,
  title,
  listProps,
}: FloatingProps) {
  return (
    <div
      ref={containerRef}
      className="relative w-full"
      onMouseDown={(e) => e.stopPropagation()}
    >
      <ResponsiveCommandMultiSelectTrigger
        selectedOptions={selectedOptions}
        placeholder={placeholder}
        disabled={disabled}
        className={className}
        triggerRef={triggerRef}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen(!open);
        }}
        aria-expanded={open}
        aria-haspopup="listbox"
      />
      {open && dropdownPosition && (
        <div
          ref={dropdownRef}
          role="dialog"
          aria-label={title}
          className={[
            "z-popover min-w-full w-max max-w-[min(90vw,420px)]",
            "rounded-lg border border-border bg-popover shadow-lg",
            "overflow-hidden",
            "animate-in fade-in-0 zoom-in-95 duration-150",
          ].join(" ")}
          style={{
            position: "fixed",
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            width: dropdownPosition.width,
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-border/60">
            <span className="text-xs font-medium text-muted-foreground tracking-wide">
              {title}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setOpen(false);
                triggerRef.current?.focus();
              }}
              aria-label="Close"
              title="Close"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
          <div className="max-h-64 overflow-y-auto">
            <ResponsiveCommandMultiSelectList {...listProps} />
          </div>
        </div>
      )}
    </div>
  );
}

interface ReplaceProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  triggerRef: React.RefObject<HTMLButtonElement>;
  selectedOptions: SelectOption[];
  placeholder: string;
  disabled: boolean;
  className?: string;
  listProps: ListProps;
}

/**
 * Inline expansion - replace variant: when open, trigger is replaced by
 * a search + list panel. Used inside modals to avoid popover positioning.
 */
export function ResponsiveCommandMultiSelectInlineReplace({
  open,
  setOpen,
  triggerRef,
  selectedOptions,
  placeholder,
  disabled,
  className,
  listProps,
}: ReplaceProps) {
  return (
    <div className="relative w-full space-y-2">
      {open ? (
        <div
          className="rounded-md border bg-popover p-2 shadow-md space-y-2"
          data-popover-content
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
          tabIndex={-1}
        >
          <div className="flex items-center justify-end mb-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setOpen(false);
              }}
              aria-label="Close"
              title="Close"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <ResponsiveCommandMultiSelectList {...listProps} />
        </div>
      ) : (
        <ResponsiveCommandMultiSelectTrigger
          selectedOptions={selectedOptions}
          placeholder={placeholder}
          disabled={disabled}
          className={className}
          triggerRef={triggerRef}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setOpen(true);
          }}
        />
      )}
    </div>
  );
}
